import React, { useState, useEffect, useCallback, useRef, ReactNode } from "react"

const getPositionFromEvent = (
    e: MouseEvent | React.MouseEvent<HTMLDivElement>,
    trackElement: HTMLElement
): number => {
    const rect = trackElement.getBoundingClientRect()
    const relativeX = e.clientX - rect.left
    return Math.max(0, Math.min(1, relativeX / rect.width))
}

type ScrubbableProps = React.HTMLAttributes<HTMLDivElement> & {
    onScrub: (position: number) => void
    onScrubStart?: () => void
    onScrubEnd?: () => void
    children: ReactNode
}

export const Scrubbable: React.FC<ScrubbableProps> = ({
    onScrub,
    onScrubStart,
    onScrubEnd,
    children,
    ...restProps
}) => {
    const trackRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)

    useEffect(() => {
        const handleGlobalMouseMove = (e: MouseEvent) => {
            const track = trackRef.current
            if (!track) return

            const position = getPositionFromEvent(e, track)
            onScrub(position)
        }

        const handleGlobalMouseUp = () => {
            setIsDragging(false)
            onScrubEnd?.()
        }

        if (isDragging) {
            window.addEventListener("mousemove", handleGlobalMouseMove)
            window.addEventListener("mouseup", handleGlobalMouseUp)
        }

        return () => {
            window.removeEventListener("mousemove", handleGlobalMouseMove)
            window.removeEventListener("mouseup", handleGlobalMouseUp)
        }
    }, [isDragging, onScrub, onScrubEnd])

    const handleMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (e.button !== 0) return
            e.preventDefault()

            const track = trackRef.current
            if (!track) return

            setIsDragging(true)
            onScrubStart?.()

            const position = getPositionFromEvent(e, track)
            onScrub(position)
        },
        [onScrub, onScrubStart]
    )

    return (
        <div ref={trackRef} onMouseDown={handleMouseDown} {...restProps}>
            {children}
        </div>
    )
}
